import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";
import {
  getSessionFindeksStatus,
  SEARCH_PROPERTY_ENUMS,
} from "@/utils/car/listingUtils";

/**
 * Car Search Service
 * Otel arama API'leri için servis
 */
export const carSearchService = {
  /**
   * Car otomatik tamamlama önerilerini getirir
   * @param {string} term - Arama terimi (örn: "Ad")
   * @param {string} languageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Usage Example:
   * autocomplete("Ad", "tr")
   * → GET /v1/meta/autocomplete/car?term=Ad&languageCode=tr
   */
  autocomplete: (
    term = "",
    languageCode = "tr",
    headers = {},
    hasErrorHandler = false,
  ) => {
    const queryParams = new URLSearchParams({
      term: term,
      languageCode: languageCode,
    });

    return request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.car.search.autocomplete}?${queryParams.toString()}`,
      payload: {},
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Car popüler lokasyon listesini getirir
   * @param {string} term - Arama terimi (örn: "Ad")
   * @param {string} languageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Usage Example:
   * autocomplete("Ad", "tr")
   * → GET /v1/meta/autocomplete/popularCarLocations?maxCount=Ad&languageCode=tr
   */
  getPopularLocations: ({
    LanguageCode = "tr",
    headers = {},
    hasErrorHandler = false,
  }) => {
    const queryParams = new URLSearchParams({
      maxCount: 5,
      LanguageCode,
    });

    return request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.car.search.getPopularLocations}?${queryParams.toString()}`,
      payload: {},
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Araç listesini getirir
   * @param pickUpLocation
   * @param dropOffLocation
   * @param pickUpTime
   * @param dropOffTime
   * @param currency
   * @param selectedFilters
   * @param selectedSorting
   * @param {string} languageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */

  getCarList: (
    {
      pickUpLocation,
      dropOffLocation,
      pickUpDateTime,
      dropOffDateTime,
      currency,
      selectedFilters,
      selectedSorting,
      ageFilter,
    },
    headers = {},
    hasErrorHandler = false,
  ) => {
    let AgeFilter = null;

    if (ageFilter.includes('+')) {
      AgeFilter = {
        MinAge: ageFilter.split('+')[0],
      }
    }

    if (!AgeFilter) {
      const [minAge, maxAge] = (ageFilter || '').split('-');

      AgeFilter = maxAge ? {
        MinAge: minAge,
        MaxAge: maxAge,
      } : {
        ExactAge: minAge
      }
    }

    const findeksStatus = getSessionFindeksStatus();
    const hideFindeks = typeof findeksStatus === 'boolean' ? !findeksStatus : false;
    const excludeProperties = [];

    if (hideFindeks) {
      excludeProperties.push(SEARCH_PROPERTY_ENUMS.FindeksRequirement);
    }

    const payload = {
      SearchForm: {
        PickUpLocation: pickUpLocation,
        PickUpDateTime: pickUpDateTime,
        DropOffLocation: dropOffLocation || pickUpLocation,
        DropOffDateTime: dropOffDateTime,
        AgeFilter
      },
      SelectedFilters: { ...selectedFilters, ExcludeByProperties: excludeProperties },
      ...(!!selectedSorting && { SelectedSorting: selectedSorting }),
      ConstructLocalizationKeys: true,
    }

    return request({
      method: HTTP_METHODS.POST,
      url: `${API_URLS.car.search.getCarList}`,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Car popüler lokasyon listesini getirir
   * @param pickUpLocation
   * @param dropOffLocation
   * @param pickUpTime
   * @param {string} languageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */

  getCarSearchQueryDetails: (
    {
      pickUpLocation,
      dropOffLocation,
      pickUpDateTime,
      dropOffDateTime,
      languageCode,
    },
    headers = {},
    hasErrorHandler = false,
  ) => {
    const payload = {
      SearchForm: {
        PickUpLocation: pickUpLocation,
        PickUpDateTime: pickUpDateTime,
        DropOffLocation: dropOffLocation || pickUpLocation,
        DropOffDateTime: dropOffDateTime,
      },
      LanguageCode: languageCode,
      ConstructLocalizationKeys: true,
    }

    return request({
      method: HTTP_METHODS.POST,
      url: `${API_URLS.car.search.getSearchQueryDetails}`,
      payload,
      headers,
      hasErrorHandler,
    });
  },
};
